import pako from 'pako';

const encoder = new TextEncoder();
const decoder = new TextDecoder();
const ivLength = 12; // AES-GCM standard IV length
const AES_GCM_ALGORITHM = 'AES-GCM';

// Encrypt a string using the provided key
export async function encrypt(data: string, key: string): Promise<Uint8Array> {
    const message = encoder.encode(data);
    const rawKey = base64ToArray(key);
    return await encryptByte(message, rawKey);
}

// Encrypt a Uint8Array (bytes) using the provided key
export async function encryptByte(
    messageAsArray: Uint8Array,
    key: Uint8Array
): Promise<Uint8Array> {
    const iv = generateIv();
    const importedKey = await importKey(key);
    const algorithm = {
        iv,
        name: AES_GCM_ALGORITHM,
    };
    const encryptedContent = await window.crypto.subtle.encrypt(
        algorithm,
        importedKey,
        messageAsArray
    );
    return mergeTypedArray(iv, new Uint8Array(encryptedContent));
}

// Decrypt an encrypted base64 string using the provided key
export async function decrypt(data: string, key: string): Promise<string> {
    const cipher = base64ToArray(data);
    const keyArray = base64ToArray(key);
    return await decryptByte(cipher, keyArray);
}

// Decrypt a Uint8Array using the provided key
export async function decryptByte(encryptedData: Uint8Array, key: Uint8Array): Promise<string> {
    const importedKey = await importKey(key);
    const iv = encryptedData.slice(0, ivLength);
    encryptedData = encryptedData.slice(ivLength);

    const algorithm = {
        iv,
        name: AES_GCM_ALGORITHM,
    };

    const decryptedData = await window.crypto.subtle.decrypt(
        algorithm,
        importedKey,
        encryptedData
    );

    const gZipHandledArrayBuffer = handleArrayBuffer(decryptedData);
    if (typeof gZipHandledArrayBuffer === 'string') {
        return gZipHandledArrayBuffer;
    }

    return decoder.decode(gZipHandledArrayBuffer);
}

// Helper function to generate a random IV for encryption
function generateIv(): Uint8Array {
    return window.crypto.getRandomValues(new Uint8Array(ivLength));
}

// Helper function to import the encryption/decryption key
async function importKey(key: Uint8Array): Promise<CryptoKey> {
    return window.crypto.subtle.importKey(
        'raw',
        key,
        AES_GCM_ALGORITHM,
        false, // key is non-extractable
        ['encrypt', 'decrypt']
    );
}

// Helper function to convert base64 string to Uint8Array
function base64ToArray(value: string): Uint8Array {
    value = value.replace(/-/g, '+').replace(/_/g, '/');
    const padding = value.length % 4;
    if (padding > 0) {
        value += '='.repeat(4 - padding);
    }
    try {
        const binaryString = window.atob(value);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }
        return bytes;
    } catch (error) {
        console.error("Invalid base64 string:", error);
        throw new Error("Failed to decode base64 string.");
    }
}

// Helper function to merge two Uint8Arrays (e.g., IV and encrypted content)
function mergeTypedArray(a: Uint8Array, b: Uint8Array): Uint8Array {
    const mergedArray = new Uint8Array(a.length + b.length);
    mergedArray.set(a);
    mergedArray.set(b, a.length);
    return mergedArray;
}

export function generateRandomString(length: number): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < length; i++) {
        const randomIndex = Math.floor(Math.random() * chars.length);
        result += chars[randomIndex];
    }
    return result;
  }

const isGzipCompressed = (arrayBuffer: ArrayBuffer) => {
    const uint8Array = new Uint8Array(arrayBuffer);
    return uint8Array[0] === 0x1f && uint8Array[1] === 0x8b;
};
  
const decompressGzip = (arrayBuffer: ArrayBuffer) => {
    try {
        const decompressedData = pako.inflate(new Uint8Array(arrayBuffer), { to: 'string' });
        return decompressedData;
    } catch (err) {
        return null;
    }
};

const handleArrayBuffer = (arrayBuffer: ArrayBuffer): string | ArrayBuffer => {
    if (isGzipCompressed(arrayBuffer)) {
        const decompressed = decompressGzip(arrayBuffer);
        if (decompressed) {
            return decompressed;
        }
    }
    return arrayBuffer;
};